/**
 * Downgrade Action - WHAT Section
 *
 * Edit mode UI for downgrading a user from a subscription.
 * When triggered, immediately downgrades from the selected subscription.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-downgrade.hbs
 *
 * Fields:
 * - downgradeItem: Object containing purchaseType and purchaseId
 *   (stored as reminderOnType and reminderOnId in the model)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';

import { ActionWhatSectionProps } from '../automationActions';
import { fetchSubscriptionItems } from '../utils/sellableItems';

/**
 * Downgrade WHAT Section - Edit mode
 */
export const DowngradeWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch subscription items
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['sellable-items', 'subscription'],
    queryFn: fetchSubscriptionItems,
    staleTime: 60000,
  });

  // V2 format: "id type" (e.g., "123 Group")
  const itemOptions = items.map((item) => ({
    value: `${item.id} ${item.type}`,
    label: `${item.name} (${item.type})`,
  }));

  // Compute value directly from the source of truth
  const selectedValue =
    automation.reminderOnType && automation.reminderOnId
      ? `${automation.reminderOnId} ${automation.reminderOnType}`
      : '';

  const handleItemChange = (value: string) => {
    if (!value) {
      setFieldValue(`automations.${index}.reminderOnType`, null);
      setFieldValue(`automations.${index}.reminderOnId`, null);
      return;
    }
    // V2 format: "id type" (split on first space)
    const spaceIndex = value.indexOf(' ');
    const purchaseId = value.substring(0, spaceIndex);
    const purchaseType = value.substring(spaceIndex + 1);
    setFieldValue(`automations.${index}.reminderOnType`, purchaseType);
    setFieldValue(`automations.${index}.reminderOnId`, purchaseId);
  };

  if (isLoading) {
    return <Skeleton variant="text" width={250} height={40} />;
  }

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.downgrade.downgradeFrom')}</Box>
        <Box sx={{ minWidth: 250 }}>
          <TextField
            value={selectedValue}
            options={itemOptions}
            placeholder={t('automations.downgrade.itemToDowngrade')}
            onChange={(e) => handleItemChange(e.target.value as string)}
          />
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * Downgrade What Summary Component
 */
export const DowngradeWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: items = [] } = useQuery({
    queryKey: ['sellable-items', 'subscription'],
    queryFn: fetchSubscriptionItems,
    staleTime: 60000,
  });

  if (!automation.reminderOnType || !automation.reminderOnId) {
    return <i>{t('automations.downgrade.noItemSelected')}</i>;
  }

  const item = items.find(
    (i) =>
      i.type === automation.reminderOnType &&
      i.id != null && i.id.toString() === automation.reminderOnId?.toString()
  );
  const itemName =
    item?.name || `${automation.reminderOnType} #${automation.reminderOnId}`;

  return (
    <>
      {t('automations.downgrade.downgradeFromItem')} <b>{itemName}</b>
    </>
  );
};
